import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { isValidName, NAME_REQUIREMENTS_MESSAGE } from "../lib/name";
import { useI18n } from "../lib/i18n";
import { NameField } from "./NameField";
import { Card, CardContent } from "./beui/SurfaceCard";
import { StatefulButton } from "./beui/StatefulButton";

export function AccountNameEditor() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const [first, setFirst] = useState(user?.firstName || ""), [last, setLast] = useState(user?.lastName || "");
  const [busy, setBusy] = useState(false), [error, setError] = useState(""), [message, setMessage] = useState("");
  useEffect(() => { setFirst(user?.firstName || ""); setLast(user?.lastName || ""); setMessage(""); setError(""); }, [user?.id]);
  async function save(event: FormEvent) {
    event.preventDefault(); setError(""); setMessage("");
    if (!isValidName(first) || !isValidName(last)) { setError(NAME_REQUIREMENTS_MESSAGE); return; }
    setBusy(true);
    try {
      const result = await api.updateMyName(first.trim(), last.trim());
      await refresh();
      setMessage(result.reKycTriggered ? "Name updated. Because this is identity information, our team will review the change (re-KYC). Your verified status stays active meanwhile." : "Name updated.");
    } catch (failure: unknown) { setError(failure instanceof Error ? failure.message : "Could not update your name."); }
    finally { setBusy(false); }
  }
  return <Card className="account-name-editor" size="compact"><CardContent>
    <h2 className="mb-3 font-bold text-navy">{t("settings.personalInformation")}</h2>
    <form onSubmit={save} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div><label className="label" htmlFor="profile-first-name">{t("settings.firstName")}</label><NameField id="profile-first-name" required placeholder="Juan" value={first} onChange={setFirst}/></div>
        <div><label className="label" htmlFor="profile-last-name">{t("settings.lastName")}</label><NameField id="profile-last-name" required placeholder="Dela Cruz" value={last} onChange={setLast}/></div>
      </div>
      <p className="text-xs text-mut">{t("settings.legalNameNotice")}</p>
      {error && <p role="alert" className="text-sm text-brand-red">{error}</p>}
      {message && <p role="status" className="text-sm text-brand-green">{message}</p>}
      <StatefulButton type="submit" state={busy ? "loading" : "idle"} className="min-h-11 bg-brand-red text-white hover:bg-brand-reddark" disabled={busy} data-testid="save-name">{t("settings.saveName")}</StatefulButton>
    </form>
  </CardContent></Card>;
}
